/**
 * useMobileEditor — centralises all state, options and mutations
 * for the "Modificar móvil" panel that appears in Trips, Shipments
 * and ShipmentDetail.  Previously that logic was copy-pasted in
 * three places; now it lives here and each consumer just wires
 * the hook result to <MobileEditorPanel> and <MobileEditorConfirmDialog>.
 */
import { useState, useMemo } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { authorizedFetch } from "@/lib/api"
import { API_URL } from "@/lib/mutation-utils"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { avatarInitials, avatarColor } from "@/lib/avatar"
import { SearchableSelect } from "@/components/ui/searchable-select"
import type { SearchableOption } from "@/components/ui/searchable-select"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog"

// ─── Shared types (consumers re-export or widen as needed) ────────────────────

export interface MobileOptionBase {
  id:                string
  isActive:          boolean
  driverId:          string | null
  truckId:           string | null
  trailerId:         string | null
  driverName:        string | null
  truckLicensePlate: string | null
  truckUnitNumber:   number | null
  operatorName:      string | null
}

export interface DriverItem { id: string; name: string; code: string }
export interface UnitItem   { id: string; licensePlate: string | null; unitNumber: number | null }

// ─── Public interface returned by the hook ────────────────────────────────────

export interface MobileEditorResult {
  /** Full mobile dropdown options: active selectable, inactive disabled with reason. */
  mobileSelectOptions: SearchableOption[]

  // Panel visibility
  showPanel:   boolean
  driverValue: string
  trailerValue: string
  /** True when the panel is open and driver or trailer differs from the original mobile values. */
  wasEdited:   boolean

  /** Driver options for the edit panel (already-assigned ones disabled). */
  driverOptions:  SearchableOption[]
  /** Trailer options for the edit panel (already-assigned ones disabled). */
  trailerOptions: SearchableOption[]
  /** Forwarded to the trailer SearchableSelect for server-side search (undefined = client-side). */
  onUnitSearch?: (term: string) => void

  /** Call when the main mobile dropdown value changes. Resets edit state. */
  onMobileSelected: (newId: string) => void
  /** Toggle the edit panel open/closed. Pre-loads current mobile values when opening. */
  togglePanel:      () => void
  setDriver:        (v: string) => void
  setTrailer:       (v: string) => void
  /** Reset all panel state — call when the parent sheet/form closes. */
  reset:            () => void

  // Confirm dialog helpers
  confirmOpen:  boolean
  openConfirm:  () => void
  closeConfirm: () => void
  /** PATCH /api/mobiles/:id with the current driverValue/trailerValue. */
  patchMobile:  () => Promise<void>
  isPending:    boolean
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface UseMobileEditorOptions {
  /** Currently selected mobile ID in the form (empty string = none). */
  mobileId:      string
  mobileOptions: MobileOptionBase[]
  driverList:    DriverItem[]
  unitList:      UnitItem[]
  /** Called with the error message when patchMobile fails. */
  onError:       (msg: string) => void
  /** Optional: called when the user types in the unit/trailer search box (server-side search). */
  onUnitSearch?: (term: string) => void
}

export function useMobileEditor({
  mobileId,
  mobileOptions,
  driverList,
  unitList,
  onError,
  onUnitSearch,
}: UseMobileEditorOptions): MobileEditorResult {
  const qc = useQueryClient()

  const [showPanel,    setShowPanel]    = useState(false)
  const [driverValue,  setDriverValue]  = useState("")
  const [trailerValue, setTrailerValue] = useState("")
  const [origDriver,   setOrigDriver]   = useState("")
  const [origTrailer,  setOrigTrailer]  = useState("")
  const [confirmOpen,  setConfirmOpen]  = useState(false)

  const wasEdited =
    showPanel &&
    (driverValue !== origDriver || trailerValue !== origTrailer)

  // ── Mutation ───────────────────────────────────────────────────────────────

  const patchMut = useMutation({
    mutationFn: async ({
      id, driverId, trailerId,
    }: { id: string; driverId: string; trailerId: string }) => {
      const res = await authorizedFetch(`${API_URL}/api/mobiles/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          driverId:  driverId  || null,
          trailerId: trailerId || null,
        }),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al modificar el móvil")
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mobiles-select"] })
      toast.success("Móvil actualizado")
    },
    onError: (err: Error) => onError(err.message),
  })

  // ── Computed options ───────────────────────────────────────────────────────

  const mobileSelectOptions = useMemo<SearchableOption[]>(() => {
    function inactiveReason(m: MobileOptionBase): string {
      const missing: string[] = []
      if (!m.driverId)  missing.push("sin chofer")
      if (!m.trailerId) missing.push("sin semi")
      return missing.length > 0 ? missing.join(", ") : "inactivo"
    }
    return [
      { value: "__none__", label: "Sin asignar" },
      ...mobileOptions.map(m => ({
        value:     m.id,
        label:     m.truckUnitNumber != null
          ? `M${m.truckUnitNumber}`
          : (m.truckLicensePlate ?? m.id),
        sublabel:  m.isActive
          ? (m.driverName ?? undefined)
          : inactiveReason(m),
        keywords:  [m.operatorName, m.driverName].filter(Boolean).join(" "),
        disabled:  !m.isActive,
        startIcon: m.operatorName ? (
          <Avatar size="sm">
            <AvatarFallback style={{ backgroundColor: avatarColor(m.operatorName), color: "white" }}>
              {avatarInitials(m.operatorName)}
            </AvatarFallback>
          </Avatar>
        ) : undefined,
      })),
    ]
  }, [mobileOptions])

  /** IDs of drivers already assigned to a mobile other than the current one. */
  const assignedDriverIds = useMemo(
    () => new Set(
      mobileOptions
        .filter(m => m.id !== mobileId && m.driverId)
        .map(m => m.driverId!)
    ),
    [mobileOptions, mobileId],
  )

  /** IDs of trailers already assigned to a mobile other than the current one. */
  const assignedTrailerIds = useMemo(
    () => new Set(
      mobileOptions
        .filter(m => m.id !== mobileId && m.trailerId)
        .map(m => m.trailerId!)
    ),
    [mobileOptions, mobileId],
  )

  const driverOptions = useMemo<SearchableOption[]>(() => [
    { value: "__none__", label: "Sin chofer" },
    ...driverList.map(d => ({
      value:    d.id,
      label:    d.name,
      sublabel: assignedDriverIds.has(d.id) ? "Ya asignado" : d.code,
      disabled: assignedDriverIds.has(d.id),
    })),
  ], [driverList, assignedDriverIds])

  const trailerOptions = useMemo<SearchableOption[]>(() => [
    { value: "__none__", label: "Sin semi" },
    ...unitList.map(u => ({
      value:    u.id,
      label:    u.licensePlate ?? `Unidad ${u.unitNumber ?? "—"}`,
      sublabel: assignedTrailerIds.has(u.id)
        ? "Ya asignado"
        : (u.unitNumber != null ? `#${u.unitNumber}` : undefined),
      disabled: assignedTrailerIds.has(u.id),
    })),
  ], [unitList, assignedTrailerIds])

  // ── Internal helpers ───────────────────────────────────────────────────────

  function loadFromMobile(id: string) {
    const m = mobileOptions.find(m => m.id === id)
    const d = m?.driverId  ?? ""
    const t = m?.trailerId ?? ""
    setDriverValue(d);  setOrigDriver(d)
    setTrailerValue(t); setOrigTrailer(t)
  }

  function clearEditState() {
    setDriverValue(""); setTrailerValue("")
    setOrigDriver("");  setOrigTrailer("")
  }

  // ── Public actions ─────────────────────────────────────────────────────────

  function onMobileSelected(newId: string) {
    if (newId) loadFromMobile(newId)
    else       clearEditState()
    setShowPanel(false)
  }

  function togglePanel() {
    if (!showPanel && mobileId) loadFromMobile(mobileId)
    setShowPanel(v => !v)
  }

  function reset() {
    setShowPanel(false)
    clearEditState()
    setConfirmOpen(false)
  }

  async function patchMobile() {
    if (!mobileId) return
    await patchMut.mutateAsync({
      id:        mobileId,
      driverId:  driverValue,
      trailerId: trailerValue,
    })
  }

  return {
    mobileSelectOptions,
    showPanel,
    driverValue,
    trailerValue,
    wasEdited,
    driverOptions,
    trailerOptions,
    ...(onUnitSearch ? { onUnitSearch } : {}),
    onMobileSelected,
    togglePanel,
    setDriver:    setDriverValue,
    setTrailer:   setTrailerValue,
    reset,
    confirmOpen,
    openConfirm:  () => setConfirmOpen(true),
    closeConfirm: () => setConfirmOpen(false),
    patchMobile,
    isPending:    patchMut.isPending,
  }
}

// ─── MobileEditorPanel ────────────────────────────────────────────────────────
// Renders the "Modificar móvil" link + the collapsible driver/trailer panel.
// Place this immediately after the main mobile <SearchableSelect>.

export function MobileEditorPanel({
  mobileId,
  editor,
}: {
  mobileId: string
  editor:   MobileEditorResult
}) {
  if (!mobileId) return null
  return (
    <>
      <button
        type="button"
        className="text-xs text-primary hover:underline text-left mt-0.5"
        onClick={editor.togglePanel}
      >
        {editor.showPanel ? "Ocultar móvil" : "Modificar móvil"}
      </button>

      {editor.showPanel && (
        <div className="grid gap-3 border rounded-md p-3 bg-muted/30">
          <p className="text-[11px] text-muted-foreground">
            Los cambios se aplicarán directamente al móvil en la tabla de móviles.
          </p>
          <div className="grid gap-1.5">
            <Label>Chofer del móvil</Label>
            <SearchableSelect
              options={editor.driverOptions}
              value={editor.driverValue || "__none__"}
              onValueChange={(v) => editor.setDriver(v === "__none__" ? "" : v)}
              placeholder="Sin chofer"
              searchPlaceholder="Buscar chofer..."
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Semi / Tráiler del móvil</Label>
            <SearchableSelect
              options={editor.trailerOptions}
              value={editor.trailerValue || "__none__"}
              onValueChange={(v) => editor.setTrailer(v === "__none__" ? "" : v)}
              placeholder="Sin semi"
              searchPlaceholder="Buscar por patente..."
              {...(editor.onUnitSearch ? { onSearchChange: editor.onUnitSearch } : {})}
            />
          </div>
        </div>
      )}
    </>
  )
}

// ─── MobileEditorConfirmDialog ────────────────────────────────────────────────
// Renders the confirmation dialog shown before patching the mobile.
// `onConfirmed(driverId, trailerId)` is called after a successful patch —
// each consumer provides its own "save" logic here.

export function MobileEditorConfirmDialog({
  editor,
  onConfirmed,
}: {
  editor:      MobileEditorResult
  onConfirmed: (driverId: string, trailerId: string) => void | Promise<void>
}) {
  return (
    <Dialog
      open={editor.confirmOpen}
      onOpenChange={(open) => { if (!open) editor.closeConfirm() }}
    >
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Modificar móvil</DialogTitle>
          <DialogDescription>
            Los cambios de chofer y/o semi se aplicarán directamente en la tabla
            de móviles y afectarán a todos los viajes que usen este móvil. ¿Continuar?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={editor.closeConfirm}>Cancelar</Button>
          <Button
            disabled={editor.isPending}
            onClick={async () => {
              try {
                await editor.patchMobile()
                await onConfirmed(editor.driverValue, editor.trailerValue)
              } catch {
                // patchMobile errors are surfaced via onError; onConfirmed errors
                // are the consumer's responsibility.
              }
            }}
          >
            {editor.isPending ? "Guardando..." : "Confirmar y guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
